import { useEffect } from 'react';
import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
  adjustFilter,
  backgroundTintStyle,
  cornerPoints,
  elementBoxStyle,
  flipTransform,
  insetCornerPoints,
  pointsToPath,
  highlightSpanStyle,
  layersInOrder,
  textFieldStyle,
  withElementDefaults,
} from '../../utils/invitationStyle';
import { BACKEND_URL } from '../../utils/api';
import { ensureDesignFonts } from '../../utils/fonts';
import { displayText } from '../../utils/affix';

export const assetUrl = (src = '') => (src.startsWith('http') || src.startsWith('data:') ? src : `${BACKEND_URL}${src}`);

/** Rect with bevel / scoop / notch corners: fill + inside border as SVG (matches the server renderer). */
const CornerShape = ({ el, scale }) => {
  const W = (el.w / 100) * PAGE_WIDTH * scale;
  const H = (el.h / 100) * PAGE_HEIGHT * scale;
  const r = el.radius * scale;
  const sw = Math.max(0, (el.stroke_width || 0) * scale);
  const outer = cornerPoints(W, H, el.corner_style, r);
  const inner = sw > 0 ? insetCornerPoints(W, H, el.corner_style, r, sw) : outer;
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {el.fill ? <path d={pointsToPath(inner)} fill={el.fill} /> : null}
      {sw > 0 ? <path d={`${pointsToPath(outer)}${pointsToPath(inner)}`} fill={el.stroke_color} fillRule="evenodd" /> : null}
    </svg>
  );
};

/** One element (shape, line or photo), absolutely positioned inside the page. */
export const ElementView = ({ element, scale, className, style, children, ...props }) => {
  const el = withElementDefaults(element);
  const cornerShape = el.type === 'rect' && el.corner_style !== 'round' && el.radius > 0;
  return (
    <div className={className} style={{ ...elementBoxStyle(el, scale), ...style }} {...props}>
      {cornerShape ? <CornerShape el={el} scale={scale || 0} /> : null}
      {el.type === 'image' && el.src ? (
        <img
          src={assetUrl(el.src)}
          alt=""
          draggable={false}
          className="h-full w-full pointer-events-none select-none"
          style={{
            objectFit: el.fit || 'cover',
            objectPosition: `${el.pos_x ?? 50}% ${el.pos_y ?? 50}%`,
            filter: adjustFilter(el, scale),
            transform: flipTransform(el),
          }}
        />
      ) : null}
      {children}
    </div>
  );
};

/** Background tint overlay; render it right after the background image. */
export const BackgroundTint = ({ background, ...props }) => {
  const style = backgroundTintStyle(background);
  return style ? <div aria-hidden="true" style={style} data-tint="1" {...props} /> : null;
};

/** The text of a field, wrapped in a per-line highlight band when that effect is on. */
export const TextContent = ({ field, text }) => {
  const value = text ?? field.text ?? '';
  const highlight = highlightSpanStyle(field);
  return highlight ? <span style={highlight}>{value}</span> : value;
};

/**
 * Read-only rendering of all design layers (elements + text) in paint order.
 * `scale` = rendered page width / 816. `getText` lets callers substitute text
 * (e.g. template default text).
 */
export const DesignLayers = ({ fields = [], elements = [], scale, getText: rawText = (f) => f.text, textShadow }) => {
  // Whatever text the caller shows (typed text or the template's sample), the fixed words around it
  // ("בן מו"ה ... הי"ו", "ליל שב"ק פרשת ...") and {day}/{week} are always filled in (utils/affix.js).
  const shown = fields.map((f) => ({ ...f, text: rawText(f) }));
  const getText = (f) => displayText({ ...f, text: rawText(f) }, shown);
  const families = fields.map((f) => f.font_family).filter(Boolean).join('|');
  useEffect(() => {
    if (families) ensureDesignFonts(families.split('|'));
  }, [families]);

  return (
    <>
      {layersInOrder(fields, elements).map(({ kind, item }, i) =>
        kind === 'element' ? (
          <ElementView key={item.id || `e${i}`} element={item} scale={scale} className="pointer-events-none" />
        ) : (
          <div
            key={item.id || `t${i}`}
            className="pointer-events-none"
            style={{
              ...textFieldStyle({ ...item, text: getText(item) }, scale),
              ...(textShadow && !item.shadow_color ? { textShadow } : {}),
            }}
          >
            <TextContent field={item} text={getText(item)} />
          </div>
        )
      )}
    </>
  );
};

export default DesignLayers;
